import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  ListGroup,
  Badge,
  Spinner,
  Row,
  Col,
  Collapse,
} from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { useI18n } from "../i18n";
import {
  formatDateShort,
  formatCurrency,
} from "../utils/formatters";
import AirportSelector from "./AirportSelector";

interface Airport {
  code: string;
  name: string;
  country: string;
}

interface FlexibleSearchResult {
  destination: string;
  destination_name: string;
  destination_country: string;
  outbound_date: string;
  outbound_departure_time: string;
  outbound_arrival_time: string;
  outbound_duration: string;
  outbound_flight_number: string;
  outbound_flight_key: string;
  outbound_price: number;
  outbound_original_price?: number;
  inbound_date: string;
  inbound_departure_time: string;
  inbound_arrival_time: string;
  inbound_duration: string;
  inbound_flight_number: string;
  inbound_flight_key: string;
  inbound_price: number;
  inbound_original_price?: number;
  total_price: number;
  total_original_price?: number;
  trip_duration_days: number;
  currency: string;
  original_currency: string;
}

interface FlexibleSearchModalProps {
  show: boolean;
  onHide: () => void;
  onTrackerAdded?: () => void;
}

const FlexibleSearchModal: React.FC<FlexibleSearchModalProps> = ({
  show,
  onHide,
  onTrackerAdded,
}) => {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [origin, setOrigin] = useState<string | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<string[]>([]);
  const [durationFrom, setDurationFrom] = useState(2);
  const [durationTo, setDurationTo] = useState(7);
  const [results, setResults] = useState<FlexibleSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creatingTracker, setCreatingTracker] = useState(false);
  const [searchFormCollapsed, setSearchFormCollapsed] = useState(false);
  const { t, get, lang } = useI18n();

  const daysOfWeek = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  // Generate next 12 months using UTC to avoid timezone issues
  const availableMonths = Array.from({ length: 12 }, (_, i) => {
    const year = currentYear;
    const month = currentMonth + i;
    const adjustedYear = year + Math.floor(month / 12);
    const adjustedMonth = month % 12;
    
    // Create date string directly to avoid timezone conversion issues
    const monthStr = (adjustedMonth + 1).toString().padStart(2, '0');
    const value = `${adjustedYear}-${monthStr}`;
    
    // Use UTC date for label formatting
    const date = new Date(Date.UTC(adjustedYear, adjustedMonth, 1));
    const label = date.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    });
    
    return { value, label };
  });

  const loadAirports = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get<Airport[]>("/api/airports", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAirports(response.data);
    } catch (err) {
      console.error("Failed to load airports:", err);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadAirports();
      // Reset form
      setSelectedMonths([]);
      setSelectedDaysOfWeek([]);
      setDurationFrom(2);
      setDurationTo(7);
      setResults([]);
      setError("");
      setSearchFormCollapsed(false);
    }
  }, [show, loadAirports]);

  const toggleMonth = (monthValue: string) => {
    setSelectedMonths(prev => {
      if (prev.includes(monthValue)) {
        // Clicking a selected month
        if (prev.length === 1) {
          // If only one month selected, clear it
          return [];
        } else if (monthValue === prev[0] || monthValue === prev[prev.length - 1]) {
          // If clicking first or last month in range, remove it and adjust range
          return prev.filter(m => m !== monthValue);
        } else {
          // Clicking a month in the middle - split the range
          // Keep only months up to the clicked one (exclusive)
          const clickedIndex = prev.indexOf(monthValue);
          return prev.slice(0, clickedIndex);
        }
      } else {
        // Adding a new month
        const newSelection = [...prev, monthValue];
        if (newSelection.length === 1) {
          return newSelection;
        }
        
        // Auto-fill range between selected months
        const sorted = newSelection.sort();
        const firstMonth = sorted[0];
        const lastMonth = sorted[sorted.length - 1];
        
        // Generate all months between first and last
        const [firstYear, firstMonthNum] = firstMonth.split('-').map(Number);
        const [lastYear, lastMonthNum] = lastMonth.split('-').map(Number);
        
        const allMonths: string[] = [];
        let currentYear = firstYear;
        let currentMonth = firstMonthNum;
        
        while (currentYear < lastYear || (currentYear === lastYear && currentMonth <= lastMonthNum)) {
          allMonths.push(`${currentYear}-${currentMonth.toString().padStart(2, '0')}`);
          currentMonth++;
          if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
          }
        }
        
        return allMonths;
      }
    });
  };

  const toggleDayOfWeek = (day: string) => {
    setSelectedDaysOfWeek(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleSearch = async () => {
    if (!origin) {
      setError(t("flexibleSearch.selectOrigin") || "Please select an origin airport");
      return;
    }

    if (selectedMonths.length === 0) {
      setError(t("flexibleSearch.selectMonths") || "Please select at least one month");
      return;
    }

    if (durationFrom > durationTo) {
      setError(t("flexibleSearch.invalidDuration") || "Invalid duration range");
      return;
    }

    setLoading(true);
    setError("");
    setResults([]);

    try {
      const token = localStorage.getItem("token");
      
      // Calculate date ranges from selected months
      const sortedMonths = [...selectedMonths].sort();
      const firstMonth = sortedMonths[0];
      const lastMonth = sortedMonths[sortedMonths.length - 1];
      
      // Parse month strings (format: YYYY-MM)
      const [firstYear, firstMonthNum] = firstMonth.split('-').map(Number);
      const [lastYear, lastMonthNum] = lastMonth.split('-').map(Number);
      
      // Outbound: first day of first month to last day of last month
      const outboundDateFrom = `${firstMonth}-01`;
      
      // Get last day of the last month
      // JavaScript months are 0-indexed, so we use lastMonthNum directly for the next month, day 0 gives previous month's last day
      const lastDayOfMonth = new Date(Date.UTC(lastYear, lastMonthNum, 0)).getUTCDate();
      const outboundDateTo = `${lastMonth}-${lastDayOfMonth.toString().padStart(2, '0')}`;
      
      // Inbound: departure + duration (using 0-indexed months for Date object)
      const inboundFromDate = new Date(Date.UTC(firstYear, firstMonthNum - 1, 1));
      inboundFromDate.setUTCDate(inboundFromDate.getUTCDate() + durationFrom);
      const inboundDateFrom = inboundFromDate.toISOString().substring(0, 10);
      
      const inboundToDate = new Date(Date.UTC(lastYear, lastMonthNum - 1, lastDayOfMonth));
      inboundToDate.setUTCDate(inboundToDate.getUTCDate() + durationTo);
      const inboundDateTo = inboundToDate.toISOString().substring(0, 10);

      const response = await axios.post<FlexibleSearchResult[]>(
        "/api/flexible-search",
        {
          origin,
          outbound_date_from: outboundDateFrom,
          outbound_date_to: outboundDateTo,
          inbound_date_from: inboundDateFrom,
          inbound_date_to: inboundDateTo,
          duration_from: durationFrom,
          duration_to: durationTo,
          days_of_week: selectedDaysOfWeek.length > 0 ? selectedDaysOfWeek : null,
          market: lang === "it" ? "it-it" : "en-gb",
          max_results: 50,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.info(t("flexibleSearch.noResults") || "No flights found for the selected criteria");
      } else {
        // Auto-collapse search form on mobile when results are loaded
        if (window.innerWidth < 768) {
          setSearchFormCollapsed(true);
        }
      }
    } catch (err: any) {
      console.error("Flexible search error:", err);
      setError(
        err.response?.data?.detail ||
        t("flexibleSearch.searchFailed") || "Search failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddTracker = async (result: FlexibleSearchResult) => {
    setCreatingTracker(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/trackers",
        {
          origin,
          destination: result.destination,
          departure_date: result.outbound_date,
          return_date: result.inbound_date,
          threshold_price: null,
          outbound_flight: {
            flight_number: result.outbound_flight_number,
            flight_key: result.outbound_flight_key || null,
            price: result.outbound_price,
            original_price: result.outbound_original_price,
            original_currency: result.original_currency,
            date: result.outbound_date,
            departure_time: result.outbound_departure_time,
            arrival_time: result.outbound_arrival_time,
            duration: result.outbound_duration,
          },
          return_flight: {
            flight_number: result.inbound_flight_number,
            flight_key: result.inbound_flight_key || null,
            price: result.inbound_price,
            original_price: result.inbound_original_price,
            original_currency: result.original_currency,
            date: result.inbound_date,
            departure_time: result.inbound_departure_time,
            arrival_time: result.inbound_arrival_time,
            duration: result.inbound_duration,
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(t("flexibleSearch.trackerAdded") || "Tracker added successfully!");
      if (onTrackerAdded) {
        onTrackerAdded();
      }
    } catch (err: any) {
      console.error("Add tracker error:", err);
      toast.error(
        err.response?.data?.detail ||
        t("flexibleSearch.addTrackerFailed") || "Failed to add tracker"
      );
    } finally {
      setCreatingTracker(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="flexible-search-modal">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <i className="bi bi-calendar-range text-primary me-2"></i>
          {t("tracker.flexibleSearch")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

        {/* Collapse toggle button - only show when results exist */}
        {results.length > 0 && (
          <div className="mb-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSearchFormCollapsed(!searchFormCollapsed)}
              className="w-100"
            >
              <i className={`bi bi-chevron-${searchFormCollapsed ? 'down' : 'up'} me-2`}></i>
              {searchFormCollapsed ? t("flexibleSearch.showSearchForm") || "Show Search Form" : t("flexibleSearch.hideSearchForm") || "Hide Search Form"}
            </Button>
          </div>
        )}

        <Collapse in={!searchFormCollapsed}>
          <div>
        <Form>
          {/* Origin Airport */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              <i className="bi bi-geo-alt me-1"></i>
              {t("flexibleSearch.origin")}
            </Form.Label>
            <AirportSelector
              airports={airports}
              value={origin || ""}
              onChange={setOrigin}
              placeholder={t("flexibleSearch.selectOrigin")}
            />
          </Form.Group>

          {/* Month Selection */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              {t("flexibleSearch.selectMonths")}
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {availableMonths.map(month => {
                const isSelected = selectedMonths.includes(month.value);
                const isFirstSelected = selectedMonths[0] === month.value;
                const isLastSelected = selectedMonths[selectedMonths.length - 1] === month.value;
                const isInRange = selectedMonths.length >= 2 && 
                  !isSelected && 
                  month.value > selectedMonths[0] && 
                  month.value < selectedMonths[selectedMonths.length - 1];
                
                return (
                  <button
                    key={month.value}
                    type="button"
                    onClick={() => toggleMonth(month.value)}
                    className={`month-selector-btn ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''} ${isFirstSelected ? 'first' : ''} ${isLastSelected ? 'last' : ''}`}
                  >
                    {month.label}
                  </button>
                );
              })}
            </div>
            <div className="text-muted small mt-2">
              <i className="bi bi-info-circle me-1"></i>
              {selectedMonths.length === 0 && (t("flexibleSearch.selectMonthsHint") || "Click to select starting month, then click another to create a range")}
              {selectedMonths.length === 1 && (t("flexibleSearch.selectEndMonth") || "Click another month to create a range, or same month to deselect")}
              {selectedMonths.length > 1 && `${selectedMonths.length} ${t("flexibleSearch.rangeSelected") || "months selected. Click endpoints to adjust range."}`}
            </div>
            <style>{`
              .month-selector-btn {
                padding: 10px 16px;
                border: 2px solid #dee2e6;
                border-radius: 8px;
                background: white;
                color: #495057;
                font-size: 0.9rem;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                position: relative;
                overflow: hidden;
              }
              .month-selector-btn:hover {
                border-color: #0d6efd;
                color: #0d6efd;
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(13, 110, 253, 0.15);
              }
              .month-selector-btn.selected {
                background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
                border-color: #0d6efd;
                color: white;
                box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
              }
              .month-selector-btn.selected:hover {
                background: linear-gradient(135deg, #0b5ed7 0%, #084298 100%);
                transform: translateY(-2px);
              }
              .month-selector-btn.in-range {
                background: linear-gradient(135deg, rgba(13, 110, 253, 0.1) 0%, rgba(13, 110, 253, 0.15) 100%);
                border-color: #0d6efd;
                border-style: dashed;
                color: #0d6efd;
              }
              .month-selector-btn.first::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background: linear-gradient(to bottom, #ffc107, #fd7e14);
              }
              .month-selector-btn.last::after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                width: 4px;
                height: 100%;
                background: linear-gradient(to bottom, #ffc107, #fd7e14);
              }
              .month-selector-btn:active {
                transform: scale(0.95);
              }
            `}</style>
          </Form.Group>

          {/* Day of Week Selection */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              {t("flexibleSearch.daysOfWeek")} <span className="text-muted fw-normal">({t("flexibleSearch.optional")})</span>
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {daysOfWeek.map(day => {
                const isSelected = selectedDaysOfWeek.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDayOfWeek(day)}
                    className={`day-selector-btn ${isSelected ? 'selected' : ''}`}
                  >
                    {t(`dayOfWeek.${day.toLowerCase()}`) || day.substring(0, 3)}
                  </button>
                );
              })}
            </div>
            <style>{`
              .day-selector-btn {
                width: 48px;
                height: 48px;
                border: 2px solid #dee2e6;
                border-radius: 50%;
                background: white;
                color: #495057;
                font-size: 0.85rem;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .day-selector-btn:hover {
                border-color: #0d6efd;
                color: #0d6efd;
                transform: scale(1.1);
                box-shadow: 0 3px 10px rgba(13, 110, 253, 0.2);
              }
              .day-selector-btn.selected {
                background: #0d6efd;
                border-color: #0d6efd;
                color: white;
                box-shadow: 0 4px 12px rgba(13, 110, 253, 0.4);
              }
              .day-selector-btn.selected:hover {
                background: #0b5ed7;
                border-color: #0b5ed7;
              }
              .day-selector-btn:active {
                transform: scale(0.95);
              }
            `}</style>
          </Form.Group>

          {/* Duration Slider */}
          <Form.Group className="mb-4">
            <Form.Label>
              {t("flexibleSearch.duration")}: {durationFrom} - {durationTo} {t("flexibleSearch.nights")}
            </Form.Label>
            <div style={{ position: 'relative', height: '50px', paddingTop: '20px' }}>
              {/* Track background with highlight */}
              <div style={{
                position: 'absolute',
                top: '34px',
                left: '10px',
                right: '10px',
                height: '4px',
                backgroundColor: '#dee2e6',
                borderRadius: '2px',
                pointerEvents: 'none',
                zIndex: 0
              }}>
                <div style={{
                  position: 'absolute',
                  left: `${((durationFrom - 1) / 30) * 100}%`,
                  width: `${((durationTo - durationFrom) / 30) * 100}%`,
                  height: '100%',
                  backgroundColor: '#0d6efd',
                  borderRadius: '2px'
                }} />
              </div>
              
              {/* Lower bound slider */}
              <input
                type="range"
                min={1}
                max={31}
                value={durationFrom}
                onChange={(e) => {
                  const newVal = parseInt(e.target.value);
                  if (newVal <= durationTo) {
                    setDurationFrom(newVal);
                  }
                }}
                style={{ 
                  position: 'absolute',
                  width: '100%',
                  top: '33px',
                  left: '0',
                  WebkitAppearance: 'none',
                  background: 'transparent',
                  zIndex: 3,
                  pointerEvents: 'auto',
                  margin: '0',
                  padding: '0'
                }}
                className="custom-range-slider"
              />
              
              {/* Upper bound slider */}
              <input
                type="range"
                min={1}
                max={31}
                value={durationTo}
                onChange={(e) => {
                  const newVal = parseInt(e.target.value);
                  if (newVal >= durationFrom) {
                    setDurationTo(newVal);
                  }
                }}
                style={{ 
                  position: 'absolute',
                  width: '100%',
                  top: '33px',
                  left: '0',
                  WebkitAppearance: 'none',
                  background: 'transparent',
                  zIndex: 3,
                  pointerEvents: 'auto',
                  margin: '0',
                  padding: '0'
                }}
                className="custom-range-slider"
              />
            </div>
            <style>{`
              .custom-range-slider {
                height: 4px;
                outline: none;
              }
              .custom-range-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background: #ffffff;
                cursor: pointer;
                border: 3px solid #0d6efd;
                box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                position: relative;
                z-index: 100;
                margin-top: -7px;
              }
              .custom-range-slider::-moz-range-thumb {
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background: #ffffff;
                cursor: pointer;
                border: 3px solid #0d6efd;
                box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              }
              .custom-range-slider::-webkit-slider-thumb:hover {
                border-color: #0b5ed7;
                transform: scale(1.2);
                box-shadow: 0 3px 10px rgba(0,0,0,0.3);
              }
              .custom-range-slider::-moz-range-thumb:hover {
                border-color: #0b5ed7;
                transform: scale(1.2);
                box-shadow: 0 3px 10px rgba(0,0,0,0.3);
              }
              .custom-range-slider::-webkit-slider-runnable-track {
                background: transparent;
                height: 4px;
                border: none;
              }
              .custom-range-slider::-moz-range-track {
                background: transparent;
                height: 4px;
                border: none;
              }
            `}</style>
          </Form.Group>

          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={loading || !origin || selectedMonths.length === 0}
            size="lg"
            className="w-100 search-flight-btn"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t("flexibleSearch.searching")}
              </>
            ) : (
              <>
                <i className="bi bi-search me-2"></i>
                {t("flexibleSearch.search")}
              </>
            )}
          </Button>
          <style>{`
            .search-flight-btn {
              background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
              border: none;
              padding: 14px 24px;
              font-weight: 600;
              letter-spacing: 0.5px;
              transition: all 0.3s ease;
              box-shadow: 0 4px 15px rgba(13, 110, 253, 0.3);
            }
            .search-flight-btn:hover:not(:disabled) {
              background: linear-gradient(135deg, #0b5ed7 0%, #084298 100%);
              box-shadow: 0 6px 20px rgba(13, 110, 253, 0.4);
              transform: translateY(-2px);
            }
            .search-flight-btn:active:not(:disabled) {
              transform: translateY(0);
            }
            .search-flight-btn:disabled {
              background: #6c757d;
              box-shadow: none;
            }
          `}</style>
        </Form>          </div>
        </Collapse>
        {/* Results */}
        {results.length > 0 && (
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0">
                <i className="bi bi-geo-alt-fill text-primary me-2"></i>
                {t("flexibleSearch.results")} 
                <Badge bg="primary" className="ms-2">{results.length}</Badge>
              </h5>
            </div>
            <ListGroup className="result-list">
              {results.map((result, index) => (
                <ListGroup.Item key={index} className="result-item border-0 shadow-sm mb-3 rounded-3">
                  <div className="d-flex flex-column flex-md-row">
                    {/* Left Column: Image + Button (Mobile) */}
                    <div className="d-flex flex-column align-items-center me-md-3 mb-3 mb-md-0" style={{ width: '120px', flexShrink: 0 }}>
                      <img
                        src={`https://www.ryanair.com/ryanair3/dam/fare-finder/destinations/airports/${result.destination}_desktop.jpg`}
                        alt={result.destination_name}
                        style={{ 
                          width: '120px', 
                          height: '80px', 
                          objectFit: 'cover', 
                          borderRadius: '8px',
                          backgroundColor: '#f0f0f0',
                          marginBottom: '8px'
                        }}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          // Try PNG fallback first
                          if (img.src.includes('_desktop.jpg')) {
                            img.src = `https://assets.ryanair.com/resources/ui/mobile/stations/${result.destination}_v2.png`;
                          } else if (!img.src.includes('via.placeholder.com')) {
                            img.onerror = null;
                            img.src = `https://via.placeholder.com/120x80/0d6efd/ffffff?text=${result.destination}`;
                          }
                        }}
                      />
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleAddTracker(result)}
                        disabled={creatingTracker}
                        className="w-100 add-tracker-btn"
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        <span className="d-none d-md-inline">{t("flexibleSearch.addTracker")}</span>
                        <span className="d-md-none">Add</span>
                      </Button>
                    </div>
                    
                    {/* Flight Details */}
                    <div className="flex-grow-1">
                      <div className="fw-bold fs-5 mb-1">
                        {result.destination_name} ({result.destination})
                      </div>
                      <div className="text-muted small mb-2">
                        <i className="bi bi-geo me-1"></i>
                        {result.destination_country}
                      </div>
                      
                      {/* Flight Times Display */}
                      <div className="mb-3">
                        {/* Outbound Flight */}
                        <div className="flight-time-row mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#0d6efd" className="flex-shrink-0">
                              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                            </svg>
                            <span className="fw-semibold text-primary me-1">{t("flexibleSearch.outbound")}:</span>
                            {result.outbound_flight_number && (
                              <Badge bg="secondary" className="me-2">{result.outbound_flight_number}</Badge>
                            )}
                            <span className="flight-time-detail">
                              {result.outbound_departure_time && result.outbound_arrival_time ? (
                                <>
                                  <i className="bi bi-clock me-1"></i>
                                  <strong>{result.outbound_departure_time}</strong> → <strong>{result.outbound_arrival_time}</strong>
                                  {result.outbound_duration && (
                                    <span className="text-muted ms-2">({result.outbound_duration})</span>
                                  )}
                                </>
                              ) : result.outbound_duration && (
                                <>
                                  <i className="bi bi-clock me-1"></i>
                                  {result.outbound_duration}
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                        {/* Inbound Flight */}
                        <div className="flight-time-row">
                          <div className="d-flex align-items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#0d6efd" className="flex-shrink-0" style={{ transform: 'scaleX(-1)' }}>
                              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                            </svg>
                            <span className="fw-semibold text-primary me-1">{t("flexibleSearch.return")}:</span>
                            {result.inbound_flight_number && (
                              <Badge bg="secondary" className="me-2">{result.inbound_flight_number}</Badge>
                            )}
                            <span className="flight-time-detail">
                              {result.inbound_departure_time && result.inbound_arrival_time ? (
                                <>
                                  <i className="bi bi-clock me-1"></i>
                                  <strong>{result.inbound_departure_time}</strong> → <strong>{result.inbound_arrival_time}</strong>
                                  {result.inbound_duration && (
                                    <span className="text-muted ms-2">({result.inbound_duration})</span>
                                  )}
                                </>
                              ) : result.inbound_duration && (
                                <>
                                  <i className="bi bi-clock me-1"></i>
                                  {result.inbound_duration}
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                        <Badge bg="info" className="px-2 py-1">
                          <i className="bi bi-calendar-event me-1"></i>
                          {formatDateShort(result.outbound_date)}
                        </Badge>
                        <i className="bi bi-arrow-right text-muted"></i>
                        <Badge bg="info" className="px-2 py-1">
                          <i className="bi bi-calendar-event me-1"></i>
                          {formatDateShort(result.inbound_date)}
                        </Badge>
                        {result.trip_duration_days > 0 && (
                          <Badge bg="light" text="dark" className="px-2 py-1">
                            <i className="bi bi-moon-stars me-1"></i>
                            {result.trip_duration_days} {result.trip_duration_days === 1 ? t("flexibleSearch.night") : t("flexibleSearch.nights")}
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-end">
                        <div className="small">
                          <span className="text-muted">{t("flexibleSearch.outbound")}: </span>
                          <span className="fw-semibold">{formatCurrency(result.outbound_price, result.outbound_original_price, result.original_currency)}</span>
                          <span className="text-muted ms-3">{t("flexibleSearch.return")}: </span>
                          <span className="fw-semibold">{formatCurrency(result.inbound_price, result.inbound_original_price, result.original_currency)}</span>
                        </div>
                        <div className="text-end">
                          <div className="text-muted small">Total</div>
                          <div className="fw-bold fs-4 text-primary">
                            {formatCurrency(result.total_price, result.total_original_price, result.original_currency)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
            <style>{`
              .result-list .result-item {
                transition: all 0.3s ease;
                cursor: default;
                border: 1px solid rgba(0,0,0,0.05) !important;
              }
              .result-list .result-item:hover {
                transform: translateY(-3px);
                box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
              }
              .add-tracker-btn {
                font-weight: 600;
                transition: all 0.2s ease;
              }
              .flight-time-row {
                font-size: 0.9rem;
              }
              .flight-time-detail {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                flex-wrap: wrap;
              }
              .add-tracker-btn:hover:not(:disabled) {
                transform: scale(1.05);
              }
              .flight-duration-container {
                font-size: 0.9rem;
                color: #6c757d;
              }
              .flight-duration {
                display: flex;
                align-items: center;
                font-weight: 500;
              }
              .flight-duration svg {
                opacity: 0.7;
              }
              /* Mobile-specific styles */
              @media (max-width: 767px) {
                .result-list .result-item > div {
                  align-items: flex-start !important;
                }
                .result-list .result-item .flex-grow-1 {
                  width: 100%;
                }
                .flight-time-row {
                  font-size: 0.85rem;
                }
                .flight-time-row .d-flex {
                  flex-wrap: wrap;
                }
                .flight-time-row .flight-time-detail {
                  width: 100%;
                  margin-top: 4px;
                  padding-left: 24px;
                }
              }
            `}</style>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={onHide}>
          <i className="bi bi-x-circle me-1"></i>
          {t("common.close")}
        </Button>
      </Modal.Footer>
      <style>{`
        .flexible-search-modal .modal-content {
          border-radius: 16px;
          border: none;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .flexible-search-modal .modal-header .btn-close {
          font-size: 0.9rem;
          opacity: 0.6;
        }
        .flexible-search-modal .modal-header .btn-close:hover {
          opacity: 1;
        }
      `}</style>
    </Modal>
  );
};

export default FlexibleSearchModal;

